import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MessageCircle, FileText, ChevronLeft, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow, format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, isSameMonth, addDays, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";

interface RecentActivity {
  id: string;
  title: string;
  type: 'conversation' | 'document';
  created_at: string;
  last_message?: string;
  content?: string;
}

type ViewMode = 'list' | 'calendar';

export function ActivitySection() {
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [monthlyActivities, setMonthlyActivities] = useState<RecentActivity[]>([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      loadRecentActivity();
      loadMonthlyActivities();
    }
  }, [user, currentMonth]);

  const loadRecentActivity = async () => {
    try {
      setLoading(true);
      
      // Buscar conversações recentes
      const { data: conversations } = await supabase
        .from('conversations')
        .select('id, title, created_at, last_message')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(3);

      // Buscar documentos recentes
      const { data: documents } = await supabase
        .from('documents')
        .select('id, title, created_at, content')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(3);

      // Combinar e ordenar por data
      const activity: RecentActivity[] = [
        ...(conversations || []).map(conv => ({
          ...conv,
          type: 'conversation' as const
        })),
        ...(documents || []).map(doc => ({
          ...doc,
          type: 'document' as const
        }))
      ]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 5);

      setRecentActivity(activity);
    } catch (error) {
      console.error('Erro ao carregar atividade recente:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlyActivities = async () => {
    try {
      const startDate = startOfMonth(currentMonth);
      const endDate = endOfMonth(currentMonth);

      // Buscar conversações do mês
      const { data: conversations } = await supabase
        .from('conversations')
        .select('id, title, created_at, last_message')
        .eq('user_id', user?.id)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false });

      // Buscar documentos do mês
      const { data: documents } = await supabase
        .from('documents')
        .select('id, title, created_at, content')
        .eq('user_id', user?.id)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false });

      // Combinar atividades
      const monthlyActivity: RecentActivity[] = [
        ...(conversations || []).map(conv => ({
          ...conv,
          type: 'conversation' as const
        })),
        ...(documents || []).map(doc => ({
          ...doc,
          type: 'document' as const
        }))
      ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setMonthlyActivities(monthlyActivity);
    } catch (error) {
      console.error('Erro ao carregar atividades mensais:', error);
    }
  };

  const handleActivityClick = (activity: RecentActivity) => {
    if (activity.type === 'conversation') {
      navigate(`/chat?conversation=${activity.id}`);
    } else {
      navigate(`/documentos?document=${activity.id}`);
    }
  };

  const getActivityIcon = (type: string) => {
    return type === 'conversation' ? MessageCircle : FileText;
  };

  const getActivityBadge = (type: string) => {
    return type === 'conversation' ? 'Chat' : 'Documento';
  };

  const getBadgeVariant = (type: string) => {
    return type === 'conversation' ? 'default' : 'secondary';
  };

  const getActivitiesForDate = (date: Date) => {
    return monthlyActivities.filter(activity => 
      isSameDay(new Date(activity.created_at), date)
    );
  };

  const hasActivitiesOnDate = (date: Date) => {
    return getActivitiesForDate(date).length > 0;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
    setSelectedDate(null);
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    
    // Preencher início da semana
    const startDay = monthStart.getDay();
    const paddingDays = Array(startDay).fill(null);
    
    return (
      <div className="space-y-4">
        {/* Header do calendário */}
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">
            {format(currentMonth, 'MMMM yyyy', { locale: ptBR })}
          </h3>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => navigateMonth('prev')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => navigateMonth('next')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Dias da semana */}
        <div className="grid grid-cols-7 gap-1 text-xs text-muted-foreground text-center">
          {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
            <div key={day} className="py-2 font-medium">
              {day}
            </div>
          ))}
        </div>

        {/* Grid do calendário */}
        <div className="grid grid-cols-7 gap-1">
          {paddingDays.map((_, index) => (
            <div key={`padding-${index}`} className="h-10" />
          ))}
          {days.map(day => {
            const hasActivities = hasActivitiesOnDate(day);
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            const dayIsToday = isToday(day);
            
            return (
              <button
                key={day.toISOString()}
                onClick={() => setSelectedDate(day)}
                className={`
                  relative h-10 w-full text-sm rounded-lg transition-all duration-200
                  hover:bg-muted/50 hover:scale-105
                  ${dayIsToday ? 'bg-primary text-primary-foreground font-semibold' : ''}
                  ${isSelected ? 'bg-accent ring-2 ring-primary ring-offset-1' : ''}
                  ${!isSameMonth(day, currentMonth) ? 'text-muted-foreground/50' : ''}
                `}
              >
                <span className="block">{format(day, 'd')}</span>
                {hasActivities && (
                  <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Atividades do dia selecionado */}
        {selectedDate && (
          <div className="space-y-2 pt-2 border-t">
            <h4 className="font-medium text-sm">
              Atividades de {format(selectedDate, 'dd/MM/yyyy')}
            </h4>
            {getActivitiesForDate(selectedDate).length === 0 ? (
              <p className="text-xs text-muted-foreground">Nenhuma atividade neste dia</p>
            ) : (
              <div className="space-y-2">
                {getActivitiesForDate(selectedDate).map(activity => {
                  const Icon = getActivityIcon(activity.type);
                  return (
                    <div
                      key={activity.id}
                      className="flex items-center gap-2 p-2 rounded-md border cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleActivityClick(activity)}
                    >
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-3 h-3 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-xs truncate">{activity.title}</p>
                        <Badge variant={getBadgeVariant(activity.type)} className="text-xs mt-1">
                          {getActivityBadge(activity.type)}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const monthsInPortuguese = [
    'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
    'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
  ];

  const getDayGradientClass = (day: Date) => {
    // Todos os dias com fundo bg-blue-200
    return 'bg-blue-200 hover:bg-blue-300';
  };

  if (loading) {
    return (
      <Card className="bg-white shadow-lg rounded-lg border-0">
        <CardContent className="p-6">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white shadow-lg rounded-lg border-0 overflow-hidden">
      <CardContent className="p-0">
        {/* Layout principal inspirado na imagem */}
        <div className="flex">
          {/* Lado esquerdo - Calendário */}
          <div className="flex-1 p-6">
            {/* Cabeçalho */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Agenda</h2>
              <p className="text-blue-600 text-sm">Organize seu tempo</p>
            </div>

            {/* Mini calendário */}
            <div className="space-y-4">
              {/* Header do calendário */}
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm text-gray-700">
                  {format(currentMonth, 'MMMM yyyy', { locale: ptBR })}
                </h3>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 hover:bg-blue-100"
                    onClick={() => navigateMonth('prev')}
                  >
                    <ChevronLeft className="h-4 w-4 text-blue-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 hover:bg-blue-100"
                    onClick={() => navigateMonth('next')}
                  >
                    <ChevronRight className="h-4 w-4 text-blue-600" />
                  </Button>
                </div>
              </div>

              {/* Dias da semana */}
              <div className="grid grid-cols-7 gap-2 text-xs text-gray-500 text-center">
                {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
                  <div key={day} className="py-2 font-medium">
                    {day}
                  </div>
                ))}
              </div>

              {/* Grid do calendário com degradê azul */}
              <div className="grid grid-cols-7 gap-1">
                {(() => {
                  const monthStart = startOfMonth(currentMonth);
                  const monthEnd = endOfMonth(currentMonth);
                  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
                  const startDay = monthStart.getDay();
                  const paddingDays = Array(startDay).fill(null);

                  return [
                    ...paddingDays.map((_, index) => (
                      <div key={`padding-${index}`} className="h-10 calendar-day-button" />
                    )),
                    ...days.map(day => {
                      const hasActivities = hasActivitiesOnDate(day);
                      const isSelected = selectedDate && isSameDay(day, selectedDate);
                      const dayIsToday = isToday(day);
                      const gradientClass = getDayGradientClass(day);
                      
                      return (
                        <button
                          key={day.toISOString()}
                          onClick={() => setSelectedDate(day)}
                          className={`
                            relative h-10 calendar-day-button text-sm rounded-lg transition-all duration-200
                            flex items-center justify-center
                            ${gradientClass}
                            ${isSelected ? 'ring-2 ring-blue-600 ring-offset-1' : ''}
                            ${dayIsToday ? 'ring-2 ring-yellow-400 shadow-lg shadow-yellow-200/50' : ''}
                            ${!isSameMonth(day, currentMonth) ? 'opacity-30' : ''}
                          `}
                        >
                          <span className={`block ${dayIsToday ? 'font-bold' : ''}`}>{format(day, 'd')}</span>
                          {dayIsToday && (
                            <div className="absolute -top-1 -right-1">
                              <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                            </div>
                          )}
                          {hasActivities && (
                            <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2">
                              <div className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                            </div>
                          )}
                        </button>
                      );
                    })
                  ];
                })()}
              </div>
            </div>
          </div>

          {/* Lado direito - Lista de atividades */}
          <div className="w-80 bg-blue-50 p-6 border-l border-blue-100">
            <h3 className="font-semibold text-gray-900 mb-4">
              {selectedDate ? `Atividades do dia ${format(selectedDate, 'dd/MM')}` : 'Atividades do dia'}
            </h3>
            
            {selectedDate ? (
              getActivitiesForDate(selectedDate).length === 0 ? (
                <p className="text-sm text-gray-500">Nenhuma atividade neste dia</p>
              ) : (
                <div className="space-y-3">
                  {getActivitiesForDate(selectedDate).map(activity => {
                    const Icon = getActivityIcon(activity.type);
                    return (
                      <div
                        key={activity.id}
                        className="flex items-center gap-3 p-3 bg-white rounded-lg border border-blue-200 cursor-pointer hover:shadow-sm transition-all"
                        onClick={() => handleActivityClick(activity)}
                      >
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-gray-900 truncate">{activity.title}</p>
                          <Badge variant={getBadgeVariant(activity.type)} className="text-xs mt-1">
                            {getActivityBadge(activity.type)}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            ) : (
              recentActivity.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Nenhuma atividade recente</p>
                  <p className="text-sm mt-1">Comece uma conversa ou crie um documento</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentActivity.slice(0, 4).map((activity) => {
                    const Icon = getActivityIcon(activity.type);
                    return (
                      <div
                        key={activity.id}
                        className="flex items-center gap-3 p-3 bg-white rounded-lg border border-blue-200 cursor-pointer hover:shadow-sm transition-all"
                        onClick={() => handleActivityClick(activity)}
                      >
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-gray-900 truncate">{activity.title}</p>
                          <Badge variant={getBadgeVariant(activity.type)} className="text-xs mt-1">
                            {getActivityBadge(activity.type)}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </div>

          {/* Barra lateral com meses */}
          <div className="w-16 bg-gradient-to-b from-blue-600 to-blue-800 flex flex-col items-center py-4 space-y-2">
            {monthsInPortuguese.map((month, index) => {
              const monthDate = new Date(currentMonth.getFullYear(), index, 1);
              const isCurrentMonth = currentMonth.getMonth() === index;
              
              return (
                <button
                  key={month}
                  onClick={() => setCurrentMonth(monthDate)}
                  className={`
                    w-10 h-8 text-xs font-medium rounded transition-all duration-200
                    ${isCurrentMonth 
                      ? 'bg-white text-blue-600 shadow-md' 
                      : 'text-white hover:bg-blue-500'
                    }
                  `}
                >
                  {month}
                </button>
              );
            })}
          </div>
        </div>

        {/* Layout responsivo para mobile */}
        <div className="block lg:hidden">
          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Agenda</h2>
              <p className="text-blue-600 text-sm">Organize seu tempo</p>
            </div>
            
            {/* Calendário primeiro em mobile */}
            {renderCalendar()}
            
            {/* Atividades depois em mobile */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-4">Atividades do dia</h3>
              {selectedDate ? (
                getActivitiesForDate(selectedDate).length === 0 ? (
                  <p className="text-sm text-gray-500">Nenhuma atividade neste dia</p>
                ) : (
                  <div className="space-y-3">
                    {getActivitiesForDate(selectedDate).map(activity => {
                      const Icon = getActivityIcon(activity.type);
                      return (
                        <div
                          key={activity.id}
                          className="flex items-center gap-3 p-3 bg-white rounded-lg border cursor-pointer hover:shadow-sm transition-all"
                          onClick={() => handleActivityClick(activity)}
                        >
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900 truncate">{activity.title}</p>
                            <Badge variant={getBadgeVariant(activity.type)} className="text-xs mt-1">
                              {getActivityBadge(activity.type)}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              ) : (
                recentActivity.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    <p>Nenhuma atividade recente</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentActivity.slice(0, 3).map((activity) => {
                      const Icon = getActivityIcon(activity.type);
                      return (
                        <div
                          key={activity.id}
                          className="flex items-center gap-3 p-3 bg-white rounded-lg border cursor-pointer hover:shadow-sm transition-all"
                          onClick={() => handleActivityClick(activity)}
                        >
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900 truncate">{activity.title}</p>
                            <Badge variant={getBadgeVariant(activity.type)} className="text-xs mt-1">
                              {getActivityBadge(activity.type)}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}